import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { SidebarComponent } from '../../components/sidebar/sidebar.component';
import { NavbarComponent } from '../../components/navbar/navbar.component';
import { ProductService } from '../../core/services/product.service';
import { EnquiryService } from '../../core/services/enquiry.service';
import { DashboardStats, Product } from '../../models/product.interface';
import { EnquiryStats } from '../../models/enquiry.interface';
import { OrderService } from '../../core/services/order.service';
import { OrderStats } from '../../models/order.model';

@Component({
  selector: 'app-dashboard',
  standalone: true,
  imports: [CommonModule, RouterModule, SidebarComponent, NavbarComponent],
  template: `
    <app-sidebar></app-sidebar>
    <app-navbar pageTitle="Dashboard" pageSubtitle="Overview & store analytics"></app-navbar>

    <div class="page-wrapper">
      <main class="main-content animate-in">

        <!-- STATS GRID -->
        <section class="stats-grid">

          <!-- Total Products -->
          <div class="stat-card glass-card glass-card-hover">
            <div class="stat-card-inner">
              <div class="icon-box icon-box-blue">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <rect x="5" y="2" width="14" height="20" rx="2" ry="2"/>
                  <line x1="12" y1="18" x2="12.01" y2="18"/>
                </svg>
              </div>
              <div class="stat-body">
                <span class="stat-label">Total Products</span>
                <div class="stat-value">{{ stats?.totalProducts || 0 }}</div>
                <span class="stat-meta">Active in catalog</span>
              </div>
            </div>
            <div class="stat-sparkline">
              <div class="bar" style="height: 40%"></div>
              <div class="bar" style="height: 65%"></div>
              <div class="bar" style="height: 45%"></div>
              <div class="bar" style="height: 80%"></div>
              <div class="bar" style="height: 70%"></div>
              <div class="bar active" style="height: 100%"></div>
            </div>
          </div>

          <!-- Customer Enquiries -->
          <div class="stat-card glass-card glass-card-hover gold-border-card">
            <div class="stat-card-inner">
              <div class="icon-box icon-box-gold">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                </svg>
              </div>
              <div class="stat-body">
                <span class="stat-label">Customer Enquiries</span>
                <div class="stat-value text-gold">{{ enquiryStats?.total || 0 }}</div>
                <a routerLink="/enquiries" class="stat-action-link">
                  {{ enquiryStats?.pending || 0 }} pending action
                  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="9 18 15 12 9 6"/></svg>
                </a>
              </div>
            </div>
            <div class="stat-sparkline gold">
              <div class="bar" style="height: 30%"></div>
              <div class="bar" style="height: 55%"></div>
              <div class="bar" style="height: 70%"></div>
              <div class="bar" style="height: 60%"></div>
              <div class="bar" style="height: 90%"></div>
              <div class="bar active" style="height: 100%"></div>
            </div>
          </div>

          <!-- In-Stock Catalog -->
          <div class="stat-card glass-card glass-card-hover">
            <div class="stat-card-inner">
              <div class="icon-box icon-box-green">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <polyline points="9 11 12 14 22 4"/>
                  <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/>
                </svg>
              </div>
              <div class="stat-body">
                <span class="stat-label">In-Stock Catalog</span>
                <div class="stat-value text-success">{{ stats?.inStockProducts || 0 }}</div>
                <span class="stat-meta">Available immediately</span>
              </div>
            </div>
            <div class="stat-sparkline green">
              <div class="bar" style="height: 60%"></div>
              <div class="bar" style="height: 75%"></div>
              <div class="bar" style="height: 55%"></div>
              <div class="bar" style="height: 80%"></div>
              <div class="bar" style="height: 70%"></div>
              <div class="bar active" style="height: 100%"></div>
            </div>
          </div>

          <!-- Collections -->
          <div class="stat-card glass-card glass-card-hover">
            <div class="stat-card-inner">
              <div class="icon-box icon-box-purple">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M3 7V5a2 2 0 0 1 2-2h2"/>
                  <path d="M17 3h2a2 2 0 0 1 2 2v2"/>
                  <path d="M21 17v2a2 2 0 0 1-2 2h-2"/>
                  <path d="M7 21H5a2 2 0 0 1-2-2v-2"/>
                  <rect x="7" y="7" width="10" height="10" rx="1"/>
                </svg>
              </div>
              <div class="stat-body">
                <span class="stat-label">Collections</span>
                <div class="stat-value text-purple">{{ stats?.categoriesCount?.length || 7 }}</div>
                <span class="stat-meta">Active categories</span>
              </div>
            </div>
            <div class="stat-sparkline purple">
              <div class="bar" style="height: 50%"></div>
              <div class="bar" style="height: 60%"></div>
              <div class="bar" style="height: 75%"></div>
              <div class="bar" style="height: 65%"></div>
              <div class="bar" style="height: 85%"></div>
              <div class="bar active" style="height: 100%"></div>
            </div>
          </div>

        </section>

        <!-- ORDER REVENUE STATS ROW -->
        <section class="stats-row" style="margin-top:-8px;margin-bottom:20px">
          <div class="stat-card glass-card glass-card-hover" style="border:1px solid rgba(34,197,94,.2)">
            <div class="stat-card-inner">
              <div class="icon-box" style="background:rgba(34,197,94,.1);color:#22c55e;border:1px solid rgba(34,197,94,.2)">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <rect x="1" y="4" width="22" height="16" rx="2"/><line x1="1" y1="10" x2="23" y2="10"/>
                </svg>
              </div>
              <div class="stat-body">
                <span class="stat-label">Total Orders</span>
                <div class="stat-value" style="color:#22c55e">{{ orderStats?.totalOrders || 0 }}</div>
                <a routerLink="/orders" class="stat-action-link">{{ orderStats?.pendingOrders || 0 }} pending &rarr;</a>
              </div>
            </div>
          </div>

          <div class="stat-card glass-card glass-card-hover" style="border:1px solid rgba(201,162,39,.3)">
            <div class="stat-card-inner">
              <div class="icon-box icon-box-gold">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
                </svg>
              </div>
              <div class="stat-body">
                <span class="stat-label">Total Revenue</span>
                <div class="stat-value text-gold">₹{{ (orderStats?.totalRevenue || 0) | number }}</div>
                <span class="stat-meta">Today: ₹{{ (orderStats?.todayRevenue || 0) | number }}</span>
              </div>
            </div>
          </div>

          <div class="stat-card glass-card glass-card-hover">
            <div class="stat-card-inner">
              <div class="icon-box" style="background:rgba(96,165,250,.1);color:#60a5fa;border:1px solid rgba(96,165,250,.2)">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <rect x="3" y="3" width="18" height="18" rx="2"/><line x1="3" y1="9" x2="21" y2="9"/>
                </svg>
              </div>
              <div class="stat-body">
                <span class="stat-label">Today's Orders</span>
                <div class="stat-value" style="color:#60a5fa">{{ orderStats?.todayOrders || 0 }}</div>
                <a routerLink="/orders" class="stat-action-link">View all orders &rarr;</a>
              </div>
            </div>
          </div>
        </section>

        <!-- QUICK ACTIONS -->
        <section class="quick-actions glass-card">
          <div class="actions-row">
            <div class="actions-left">
              <h3 class="section-title">Quick Actions</h3>
              <p class="sub-text">Manage catalog, enquiries & store content</p>
            </div>
            <div class="actions-right">
              <a routerLink="/enquiries" class="btn-secondary">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                </svg>
                View Enquiries
              </a>
              <button (click)="reseedDatabase()" class="btn-secondary" [disabled]="seeding">
                <svg *ngIf="!seeding" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <polyline points="23 4 23 10 17 10"/>
                  <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/>
                </svg>
                <span class="spinner" *ngIf="seeding"></span>
                {{ seeding ? 'Seeding…' : 'Reset DB Seed' }}
              </button>
              <a routerLink="/products/new" class="btn-gold">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <circle cx="12" cy="12" r="10"/>
                  <line x1="12" y1="8" x2="12" y2="16"/>
                  <line x1="8" y1="12" x2="16" y2="12"/>
                </svg>
                Add Product
              </a>
            </div>
          </div>

          <div *ngIf="seedMessage" class="alert alert-gold" style="margin-top: 14px;">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <polyline points="9 11 12 14 22 4"/>
              <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/>
            </svg>
            {{ seedMessage }}
          </div>
        </section>

        <!-- RECENT PRODUCTS -->
        <section class="glass-card">
          <div class="table-section-header">
            <div>
              <h3 class="font-serif title-gold" style="font-size: 17px; font-weight: 700;">Recent Products</h3>
              <p class="sub-text" style="margin-top: 2px;">Latest additions to Shell Muze catalog</p>
            </div>
            <a routerLink="/products" class="view-all-btn">
              View All
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="9 18 15 12 9 6"/></svg>
            </a>
          </div>

          <div class="table-wrapper">
            <table class="custom-table">
              <thead>
                <tr>
                  <th>Product</th>
                  <th>Collection</th>
                  <th>Price</th>
                  <th>Badge</th>
                  <th>Stock</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                <tr *ngFor="let product of recentProducts">
                  <td>
                    <div class="product-cell">
                      <div class="thumb-box" [style.background]="product.bg || '#131C2E'">
                        <img *ngIf="product.imageUrl" [src]="product.imageUrl" [alt]="product.title" />
                        <svg *ngIf="!product.imageUrl" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" style="color: var(--text-muted);">
                          <rect x="5" y="2" width="14" height="20" rx="2" ry="2"/>
                          <line x1="12" y1="18" x2="12.01" y2="18"/>
                        </svg>
                      </div>
                      <div>
                        <div class="product-title font-serif">{{ product.title }}</div>
                        <div class="product-label">{{ product.label || 'SHELL MUZE' }}</div>
                      </div>
                    </div>
                  </td>
                  <td>
                    <span class="badge badge-gold">{{ product.category }}</span>
                  </td>
                  <td>
                    <span class="price-main">₹{{ product.price }}</span>
                    <span class="price-slash" *ngIf="product.slashPrice">₹{{ product.slashPrice }}</span>
                  </td>
                  <td>
                    <span class="badge badge-blue">{{ product.badge || 'Sale' }}</span>
                  </td>
                  <td>
                    <span [class]="product.inStock !== false ? 'badge badge-success' : 'badge badge-danger'">
                      {{ product.inStock !== false ? 'In Stock' : 'Out of Stock' }}
                    </span>
                  </td>
                  <td>
                    <a [routerLink]="['/products/edit', product._id]" class="btn-secondary btn-icon" title="Edit">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                      </svg>
                    </a>
                  </td>
                </tr>

                <tr *ngIf="recentProducts.length === 0 && !loading">
                  <td colspan="6">
                    <div class="empty-state">
                      <div class="empty-state-icon">
                        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
                          <rect x="5" y="2" width="14" height="20" rx="2" ry="2"/>
                          <line x1="12" y1="18" x2="12.01" y2="18"/>
                        </svg>
                      </div>
                      <h4>No products yet</h4>
                      <p>Add your first product to the catalog</p>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

      </main>
    </div>
  `,
  styles: [`
    .stats-grid {
      display: grid;
      grid-template-columns: repeat(4, 1fr);
      gap: 20px;
    }

    @media (max-width: 1280px) {
      .stats-grid { grid-template-columns: repeat(2, 1fr); }
    }

    @media (max-width: 720px) {
      .stats-grid { grid-template-columns: 1fr; }
    }

    /* Stat Cards */
    .stat-card {
      padding: 22px 22px 18px;
      display: flex;
      flex-direction: column;
      gap: 14px;
      overflow: hidden;
      position: relative;
    }

    .stat-card-inner {
      display: flex;
      align-items: flex-start;
      gap: 16px;
    }

    .stat-body {
      display: flex;
      flex-direction: column;
      gap: 2px;
      flex: 1;
      min-width: 0;
    }

    .stat-label {
      font-size: 11px;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 0.08em;
      color: var(--text-muted);
    }

    .stat-value {
      font-size: 32px;
      font-weight: 800;
      line-height: 1.1;
      margin: 4px 0 2px;
      letter-spacing: -0.02em;
    }

    .stat-meta {
      font-size: 11.5px;
      color: var(--text-muted);
    }

    .stat-action-link {
      font-size: 11.5px;
      color: var(--accent-gold);
      font-weight: 700;
      display: inline-flex;
      align-items: center;
      gap: 3px;
      text-decoration: none;
      margin-top: 1px;
    }

    .stat-action-link:hover {
      text-decoration: underline;
    }

    /* Sparklines */
    .stat-sparkline {
      display: flex;
      align-items: flex-end;
      gap: 3px;
      height: 32px;
    }

    .stat-sparkline .bar {
      flex: 1;
      border-radius: 2px;
      background: rgba(96, 165, 250, 0.15);
      transition: all 0.3s ease;
    }

    .stat-sparkline .bar.active {
      background: var(--blue);
    }

    .stat-sparkline.gold .bar { background: var(--accent-gold-glow); }
    .stat-sparkline.gold .bar.active { background: var(--accent-gold); }
    .stat-sparkline.green .bar { background: var(--success-bg); }
    .stat-sparkline.green .bar.active { background: var(--success); }
    .stat-sparkline.purple .bar { background: var(--purple-bg); }
    .stat-sparkline.purple .bar.active { background: var(--purple); }

    /* Quick Actions */
    .quick-actions {
      padding: 22px 28px;
    }

    .actions-row {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 20px;
      flex-wrap: wrap;
    }

    .actions-right {
      display: flex;
      align-items: center;
      gap: 10px;
      flex-wrap: wrap;
    }

    /* Table section */
    .table-section-header {
      display: flex;
      align-items: flex-start;
      justify-content: space-between;
      padding: 22px 24px 16px;
      border-bottom: 1px solid var(--border-subtle);
    }

    .view-all-btn {
      font-size: 12px;
      font-weight: 700;
      color: var(--accent-gold);
      display: inline-flex;
      align-items: center;
      gap: 4px;
      text-decoration: none;
      padding: 6px 12px;
      border-radius: var(--radius-sm);
      transition: background var(--transition-fast);
    }

    .view-all-btn:hover {
      background: var(--accent-gold-glow);
    }

    .table-wrapper {
      padding: 0;
    }

    /* Product row */
    .product-cell {
      display: flex;
      align-items: center;
      gap: 14px;
    }

    .product-title {
      font-size: 13.5px;
      font-weight: 700;
      color: var(--text-primary);
    }

    .product-label {
      font-size: 10px;
      color: var(--text-muted);
      letter-spacing: 0.08em;
      text-transform: uppercase;
      margin-top: 1px;
    }

    .price-main {
      font-weight: 700;
      font-size: 13.5px;
      color: var(--text-primary);
    }

    .price-slash {
      font-size: 11px;
      color: var(--text-muted);
      text-decoration: line-through;
      margin-left: 6px;
    }
  `]
})
export class DashboardComponent implements OnInit {
  stats: DashboardStats | null = null;
  enquiryStats: EnquiryStats | null = null;
  orderStats: OrderStats | null = null;
  recentProducts: Product[] = [];
  loading = true;
  seeding = false;
  seedMessage = '';

  constructor(
    private productService: ProductService,
    private enquiryService: EnquiryService,
    private orderService: OrderService
  ) {}

  ngOnInit(): void {
    this.loadData();
  }

  loadData(): void {
    this.productService.getDashboardStats().subscribe({
      next: (data) => (this.stats = data),
      error: (err) => console.error(err)
    });

    this.enquiryService.getStats().subscribe({
      next: (stats) => (this.enquiryStats = stats),
      error: (err) => console.error(err)
    });

    this.orderService.getOrderStats().subscribe({
      next: (stats) => (this.orderStats = stats),
      error: (err) => console.error(err)
    });

    this.productService.getProducts({ page: 1 }).subscribe({
      next: (res) => {
        this.recentProducts = res.products.slice(0, 6);
        this.loading = false;
      },
      error: (err) => {
        this.loading = false;
        console.error(err);
      }
    });
  }

  reseedDatabase(): void {
    if (!confirm('Are you sure you want to reset and re-seed the local MongoDB database with default products and categories?')) {
      return;
    }

    this.seeding = true;
    this.seedMessage = '';

    this.productService.triggerSeedDatabase().subscribe({
      next: (res) => {
        this.seeding = false;
        this.seedMessage = `Successfully seeded database with ${res.productsCount} products and ${res.categoriesCount} categories!`;
        this.loadData();
      },
      error: (err) => {
        this.seeding = false;
        this.seedMessage = 'Failed to seed database: ' + (err.error?.message || err.message);
      }
    });
  }
}
